


import React, { useEffect, useState } from "react";
// import Header from "../../Layouts/Header";
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";
import { BarChartOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { Drawer, Modal, Popover, Tabs } from "antd";  // ← added Drawer

import InfoDetails from "./InfoDetails";
import Graph from "./Graph";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const { TabPane } = Tabs;

const COLORS = [
  "#f0ad4e",
  "#ff9800",
  "#4caf50",
  "#dc3545",
  "#b22222",
];

// Gender may arrive as a code ("M"/"F"/"O") or a word ("Male"/"Female"/"Others"); match on the
// first letter. Anything that isn't male/female — including "Others" — gets the combined range.
const genderKey = (gender) => String(gender || '').trim().charAt(0).toUpperCase();

const getHaemoglobinRanges = (gender) => {
  const g = genderKey(gender);
  if (g === 'M') {
    return {
      ranges: [
        { min: 0, max: 10, color: COLORS[4] },
        { min: 10.1, max: 13.4, color: COLORS[0] },
        { min: 13.5, max: 15.5, color: COLORS[2] },
        { min: 15.6, max: 17.5, color: COLORS[2] },
        { min: 17.6, max: 19, color: COLORS[1] },
        { min: 19.1, max: 25, color: COLORS[3] },
      ],
      healthyRange: "13.5–17.5 g/dL",
    };
  }
  if (g === 'F') {
    return {
      ranges: [
        { min: 0, max: 9.5, color: COLORS[4] },
        { min: 9.6, max: 11.9, color: COLORS[0] },
        { min: 12.0, max: 13.5, color: COLORS[2] },
        { min: 13.6, max: 15.5, color: COLORS[2] },
        { min: 15.6, max: 17, color: COLORS[1] },
        { min: 17.1, max: 25, color: COLORS[3] },
      ],
      healthyRange: "12.0–15.5 g/dL",
    };
  }
  return {
    ranges: [
      { min: 0, max: 10, color: COLORS[4] },
      { min: 10.1, max: 12, color: COLORS[0] },
      { min: 12.1, max: 17.5, color: COLORS[2] },
      { min: 17.6, max: 19, color: COLORS[1] },
      { min: 19.1, max: 25, color: COLORS[3] },
    ],
    healthyRange: "12.0–17.5 g/dL",
  };
};

const getHealthyRangeText = (gender) => {
  const g = genderKey(gender);
  if (g === 'M') return "13.5–17.5 g/dL";
  if (g === 'F') return "12.0–15.5 g/dL";
  return "12.0–17.5 g/dL";
};

const systolicBPRanges = [
  { min: 0, max: 59, color: COLORS[0] },
  { min: 60, max: 79, color: COLORS[1] },
  { min: 80, max: 105, color: COLORS[2] },
  { min: 106, max: 130, color: COLORS[2] },
  { min: 131, max: 160, color: COLORS[3] },
  { min: 161, max: 200, color: COLORS[4] },
];

const diastolicBPRanges = [
  { min: 0, max: 30, color: COLORS[0] },
  { min: 31, max: 49, color: COLORS[1] },
  { min: 50, max: 70, color: COLORS[2] },
  { min: 71, max: 90, color: COLORS[2] },
  { min: 91, max: 100, color: COLORS[3] },
  { min: 101, max: 120, color: COLORS[4] },
];

const heartRateRanges = [
  { min: 0, max: 29, color: COLORS[0] },
  { min: 30, max: 59, color: COLORS[1] },
  { min: 60, max: 75, color: COLORS[2] },
  { min: 76, max: 100, color: COLORS[2] },
  { min: 101, max: 120, color: COLORS[3] },
  { min: 121, max: 150, color: COLORS[4] },
];

const breathingRateRanges = [
  { min: 0, max: 4, color: COLORS[0] },
  { min: 5, max: 11, color: COLORS[1] },
  { min: 12, max: 15, color: COLORS[2] },
  { min: 16, max: 20, color: COLORS[2] },
  { min: 21, max: 30, color: COLORS[3] },
  { min: 31, max: 40, color: COLORS[4] },
];

const hrvRanges = [
  { min: 0, max: 19, color: COLORS[0] },
  { min: 20, max: 49, color: COLORS[1] },
  { min: 50, max: 75, color: COLORS[2] },
  { min: 76, max: 110, color: COLORS[2] },
  { min: 111, max: 160, color: COLORS[3] },
  { min: 161, max: 200, color: COLORS[4] },
];

const bmiRanges = [
  { min: 0, max: 14.9, color: COLORS[0] },
  { min: 15, max: 18.4, color: COLORS[1] },
  { min: 18.5, max: 24.9, color: COLORS[2] },
  { min: 25, max: 29.9, color: COLORS[3] },
  { min: 30, max: 39.9, color: COLORS[3] },
  { min: 40, max: 50, color: COLORS[4] },
];

const stressIndexRanges = [
  { min: 0, max: 50, color: COLORS[2] },
  { min: 51, max: 75, color: COLORS[3] },
  { min: 76, max: 100, color: COLORS[4] },
];

const fatigueIndexRange = [
  { min: 0, max: 30, color: COLORS[2] },
  { min: 31, max: 60, color: COLORS[1] },
  { min: 61, max: 80, color: COLORS[3] },
  { min: 81, max: 100, color: COLORS[4] },
];

const recoveryIndexRange = [
  { min: 0, max: 20, color: COLORS[4] },
  { min: 21, max: 40, color: COLORS[3] },
  { min: 41, max: 70, color: COLORS[1] },
  { min: 71, max: 100, color: COLORS[2] },
];

const oxygenLevelRanges = [
  { min: 90, max: 94, color: COLORS[3] },
  { min: 95, max: 100, color: COLORS[2] },
];

// Detect mobile once at module level
const isMobile = () => window.innerWidth < 768;

const GaugeCard = ({
  title,
  value,
  minValue,
  maxValue,
  ranges,
  unit,
  status,
  healthyRange,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  graphData,
}) => {
  console.log(status, "rfgth");

  const [selectedGraph, setSelectedGraph] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [mobile, setMobile] = useState(isMobile());

  // Update mobile state on resize
  useEffect(() => {
    const handler = () => setMobile(isMobile());
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Lock body scroll when drawer/popover open on mobile
  useEffect(() => {
    if (infoOpen && mobile) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    };
  }, [infoOpen, mobile]);

  const storedData = JSON.parse(localStorage.getItem("patientInfo"));

  const getColorForValue = (value) => {
    for (let i = 0; i < ranges.length; i++) {
      const range = ranges[i];
      if (value >= range.min && value <= range.max) {
        return range.color;
      }
    }
    return "#b22222";
  };

  const getField = (title) => {
    const fieldMap = {
      "Haemoglobin": "haemoglobin",
      "Systolic Blood Pressure": "systolic_blood_pressure",
      "Diastolic Blood Pressure": "diastolic_blood_pressure",
      "Heart Rate": "predicted_heart_rate",
      "Breathing Rate": "breathing_rate",
      "Heart Rate Variability": "heart_rate_variability",
      "BMI": "bmi_score",
      "Oxygen Level": "oxygen_level",
      "Stress Index": "curebay_stress_index",
      "Fatigue Risk": "fatigue_risk_score",
      "Anxiety Risk": "anxiety_risk_score",
      "Burnout Risk": "burnout_risk_score",
      "Recovery Score": "recovery_score",
    };
    return fieldMap[title] || "";
  };

  const openModal = (graphTitle) => {
    setSelectedGraph(graphTitle);
    setIsModalVisible(true);
    console.log("Opening Modal for:", graphTitle);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setSelectedGraph(null);
  };

  console.log(selectedGraph, "dfcvgbh");

  const renderInfoContent = (title) => (
    <div style={{ width: mobile ? '100%' : '300px' }}>
      <InfoDetails title={title} />
    </div>
  );

  const isStressIndex = title === "Stress Index";

  let gaugeValue;
  let displayValue;
  let adjustedMin = minValue;
  let adjustedMax = maxValue;

  if (isStressIndex) {
    gaugeValue = value;
    displayValue = value;
  } else {
    gaugeValue = value;
    displayValue = value;
  }

  const color = getColorForValue(gaugeValue);

  return (
    <div className="relative bg-gradient-to-b from-[#abc9fb] to-White rounded-2xl shadow-lg p-6 flex flex-col items-center justify-center">

      {/* FIX 1: Badge top-LEFT — info icon top-right always tappable */}
      {title === "Haemoglobin" && (
        <div
          className="absolute top-2 left-2 px-3 py-1 text-xs font-semibold text-white rounded-lg"
          style={{
            background: "var(--color-brand-primary)",
            color: "#fff",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
          }}
        >
          New
        </div>
      )}

      {/* Title */}
      <h3 className="text-xl sm:text-2xl font-bold text-primaryColor mb-4 flex items-center gap-2">
        {title}

        {/* <Popover ... /> — commented code preserved as-is */}
        {/* <BarChartOutlined ... /> */}
        {/* <Modal ... /> */}

        {/* MOBILE: bottom Drawer. DESKTOP: Popover — same as before */}
        {mobile ? (
          <>
            <InfoCircleOutlined
              className="text-primaryColor cursor-pointer flex-shrink-0"
              onClick={() => setInfoOpen(true)}
            />
            <Drawer
              title={
                <span style={{ fontSize: '15px', fontWeight: '700', color: '#1E3A8A' }}>
                  {title}
                </span>
              }
              placement="bottom"
              open={infoOpen}
              onClose={() => setInfoOpen(false)}
              height="auto"
              styles={{
                body: { padding: '12px 16px 24px', overflowY: 'auto', maxHeight: '70vh' },
                header: { padding: '12px 16px', borderBottom: '1px solid #e5e7eb' },
              }}
              style={{ borderRadius: '16px 16px 0 0' }}
              zIndex={10001}
            >
              <InfoDetails title={title} />
            </Drawer>
          </>
        ) : (
          <Popover
            content={renderInfoContent(title)}
            trigger="click"
            placement="top"
            open={infoOpen}
            onOpenChange={setInfoOpen}
            overlayStyle={{ zIndex: 10001, maxWidth: '320px' }}
            overlayInnerStyle={{ padding: '12px', borderRadius: '12px' }}
          >
            <InfoCircleOutlined className="text-primaryColor cursor-pointer flex-shrink-0" />
          </Popover>
        )}
      </h3>

      {/* Gauge with Glow Effect */}
      <div
        style={{
          width: "min(200px, 65vw)",
          height: "min(200px, 65vw)",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: `0 0 15px 5px ${color}80`,
          borderRadius: "50%",
          backgroundColor: "white",
        }}
        className="mt-6 mb-6"
      >
        <Gauge
          value={gaugeValue}
          min={adjustedMin}
          max={adjustedMax}
          thickness={12}
          sx={{
            [`& .${gaugeClasses.valueText}`]: {
              fontSize: "32px",
              fill: color,
              fontWeight: "bold",
              textAnchor: "middle",
              dominantBaseline: "central",
            },
            [`& .${gaugeClasses.valueArc}`]: {
              fill: color,
              filter: `drop-shadow(0 0 10px ${color}80)`,
            },
            [`& .${gaugeClasses.referenceArc}`]: {
              fill: "#e0e7ff",
            },
          }}
          renderValueLabel={() => (
            <text
              x="50%"
              y="50%"
              fill={color}
              fontSize="32px"
              fontWeight="bold"
              textAnchor="middle"
              dominantBaseline="central"
            >
              {isStressIndex ? `${displayValue}%` : displayValue}
            </text>
          )}
        />
      </div>

      {/* Unit */}
      {unit && (
        <p className="text-sm sm:text-base font-medium mt-4 text-gray-700">
          {unit}
        </p>
      )}

      {/* Healthy Range */}
      <p className="text-sm sm:text-base text-gray-600 mt-2">
        Healthy Range: <strong>{healthyRange}</strong>
      </p>

      {/* Status Button */}
      <button className="text-sm sm:text-base font-semibold mt-4 bg-White text-[#313131] rounded-full px-4 py-2 shadow-md transition-all">
        {status}
      </button>
    </div>
  );
};

const SmartVitalsReport = (props) => {
  const navigate = useNavigate();
  const [vitalsData, setVitalsData] = useState(null);
  const [graphData, setGraphData] = useState([]);
  const [fromDate, setFromDate] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));

  useEffect(() => {
    if (props.vitalsData) {
      setVitalsData(props.vitalsData);
    } else if (props.location?.state?.responseData) {
      setVitalsData(props.location.state.responseData);
    }
  }, [props.vitalsData, props.location?.state]);

  const storedData = JSON.parse(localStorage.getItem("patientInfo"));
  const propsdata = props.vitalsData
    ? { responseData: props.vitalsData, pastVitals: true }
    : props.location?.state;

  const date = moment(propsdata?.responseData?.created_date).format("DD-MM-YYYY");

  const toPercent = (v) => {
    if (v === null || v === undefined) return 0;
    const num = parseFloat(v);
    return num <= 1 ? num * 100 : num;
  };

  useEffect(() => {
    if (!storedData?.data?.code) return;
    fetch(`
       ${process.env.REACT_APP_SMARTVITAL}user-metrics?patient_code=${storedData?.data?.code}&start_date=${fromDate}&end_date=${toDate}`)
      .then((res) => {
        if (!res.ok) throw new Error('Network response was not ok');
        return res.json();
      })
      .then((data) => {
        setGraphData(data?.data);
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      });
  }, [fromDate, toDate]);

  const handleRoute = () => {
    navigate("/smart-vital-instr");
  };

  if (!vitalsData) {
    return <div className="text-center p-6">Loading vitals data...</div>;
  }

  const haemoglobinConfig = getHaemoglobinRanges(vitalsData.gender);

  return (
    <div className={`p-4 sm:p-6 ${props.isModal ? '' : 'min-h-screen mt-20'} bg-gradient-to-b from-[#E3F2FD] to-white`}>
      {!props.isModal
      //  && <Header />
      }

      {/* Title Section */}
      <div className="flex flex-col sm:flex-row items-center justify-between mb-6 px-2 sm:px-0">
        <h1 className="flex-1 text-center sm:text-center text-2xl sm:text-4xl font-extrabold text-[#1E3A8A]">
          {propsdata?.pastVitals ? (
            <><span className="text-secondaryColor">Vitals</span></>
          ) : (
            <>My <span className="text-secondaryColor">Vitals</span></>
          )}
        </h1>
      </div>

      <div className="p-4 mx-auto max-w-[118rem]">
        {/* Sticky tab bar */}
        <div
          className="sticky top-0 z-[100] bg-white"
          style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
        >
          <Tabs defaultActiveKey="1" centered className="mb-6 text-lg">

            {/* Physical Index Tab */}
            <TabPane tab={<span className="text-primaryColor text-lg sm:text-2xl font-semibold">Physical Index</span>} key="1">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">

                {vitalsData?.haemoglobin !== null &&
                  vitalsData?.haemoglobin !== undefined &&
                  vitalsData?.haemoglobin !== "" && (
                    <GaugeCard
                      title="Haemoglobin"
                      value={parseFloat(vitalsData.haemoglobin)}
                      minValue={0}
                      maxValue={25}
                      ranges={haemoglobinConfig.ranges}
                      unit="g/dL"
                      status={vitalsData.haemoglobin_status}
                      healthyRange={getHealthyRangeText(vitalsData.gender)}
                      fromDate={fromDate}
                      setFromDate={setFromDate}
                      toDate={toDate}
                      setToDate={setToDate}
                      graphData={graphData}
                    />
                  )}

                <GaugeCard
                  title="Systolic Blood Pressure"
                  value={parseFloat((vitalsData.blood_pressure || "0/0").split("/")[0])}
                  minValue={0}
                  maxValue={160}
                  ranges={systolicBPRanges}
                  unit="mm Hg"
                  status={vitalsData.blood_pressure_status}
                  healthyRange="90-120 mm Hg"
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  toDate={toDate}
                  setToDate={setToDate}
                  graphData={graphData}
                />

                <GaugeCard
                  title="Diastolic Blood Pressure"
                  value={parseFloat((vitalsData.blood_pressure || "0/0").split("/")[1])}
                  minValue={0}
                  maxValue={100}
                  ranges={diastolicBPRanges}
                  unit="mm Hg"
                  status={vitalsData.blood_pressure_status}
                  healthyRange="60-80 mm Hg"
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  toDate={toDate}
                  setToDate={setToDate}
                  graphData={graphData}
                />

                <GaugeCard
                  title="Heart Rate"
                  value={parseFloat(vitalsData.predicted_heart_rate)}
                  minValue={0}
                  maxValue={120}
                  ranges={heartRateRanges}
                  status={vitalsData.heart_rate_status}
                  unit="bpm"
                  healthyRange="60-100 bpm"
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  toDate={toDate}
                  setToDate={setToDate}
                  graphData={graphData}
                />

                <GaugeCard
                  title="Breathing Rate"
                  value={parseFloat(vitalsData.breathing_rate)}
                  minValue={0}
                  maxValue={30}
                  ranges={breathingRateRanges}
                  unit="breaths/min"
                  status={vitalsData.breathing_rate_status}
                  healthyRange="12-20 breaths/min"
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  toDate={toDate}
                  setToDate={setToDate}
                  graphData={graphData}
                />

                <GaugeCard
                  title="Heart Rate Variability"
                  value={parseFloat(vitalsData.heart_rate_variability)}
                  minValue={0}
                  maxValue={160}
                  ranges={hrvRanges}
                  unit="ms"
                  status={vitalsData.heart_rate_variability_status}
                  healthyRange="50-100 ms"
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  toDate={toDate}
                  setToDate={setToDate}
                  graphData={graphData}
                />

                <GaugeCard
                  title="BMI"
                  value={parseFloat(vitalsData.bmi_score)}
                  minValue={0}
                  maxValue={40}
                  ranges={bmiRanges}
                  unit="kg/m²"
                  status={vitalsData.bmi_status}
                  healthyRange="18.5-25"
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  toDate={toDate}
                  setToDate={setToDate}
                  graphData={graphData}
                />

                <GaugeCard
                  title="Oxygen Level"
                  value={vitalsData.oxygen_level}
                  minValue={90}
                  maxValue={100}
                  ranges={oxygenLevelRanges}
                  unit="SpO2 (%)"
                  status={vitalsData?.oxygen_level_status}
                  healthyRange="95-100%"
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  toDate={toDate}
                  setToDate={setToDate}
                  graphData={graphData}
                />

                <GaugeCard
                  title="Stress Index"
                  value={vitalsData.curebay_stress_index}
                  minValue={0}
                  maxValue={100}
                  ranges={stressIndexRanges}
                  unit="%"
                  status={vitalsData?.curebay_stress_status}
                  healthyRange="0-25%"
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  toDate={toDate}
                  setToDate={setToDate}
                  graphData={graphData}
                />
              </div>
            </TabPane>

            {(vitalsData?.fatigue_risk_score || vitalsData?.anxiety_risk_score || vitalsData?.burnout_risk_score || vitalsData?.recovery_score) &&
              (<TabPane tab={<span className="text-primaryColor text-lg sm:text-2xl font-semibold">Wellness Index</span>} key="2">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">

                  <GaugeCard
                    title="Fatigue Risk"
                    value={toPercent(vitalsData.fatigue_risk_score)}
                    minValue={0}
                    maxValue={100}
                    ranges={fatigueIndexRange}
                    unit="%"
                    status={vitalsData.fatigue_risk_status}
                    healthyRange="Less than 30%"
                    fromDate={fromDate}
                    setFromDate={setFromDate}
                    toDate={toDate}
                    setToDate={setToDate}
                    graphData={graphData}
                  />

                  <GaugeCard
                    title="Anxiety Risk"
                    value={toPercent(vitalsData.anxiety_risk_score)}
                    minValue={0}
                    maxValue={100}
                    ranges={fatigueIndexRange}
                    unit="%"
                    status={vitalsData.anxiety_risk_status}
                    healthyRange="Less than 30%"
                    fromDate={fromDate}
                    setFromDate={setFromDate}
                    toDate={toDate}
                    setToDate={setToDate}
                    graphData={graphData}
                  />

                  <GaugeCard
                    title="Burnout Risk"
                    value={toPercent(vitalsData.burnout_risk_score)}
                    minValue={0}
                    maxValue={100}
                    ranges={fatigueIndexRange}
                    unit="%"
                    status={vitalsData.burnout_risk_status}
                    healthyRange="Less than 30%"
                    fromDate={fromDate}
                    setFromDate={setFromDate}
                    toDate={toDate}
                    setToDate={setToDate}
                    graphData={graphData}
                  />

                  <GaugeCard
                    title="Recovery Score"
                    value={toPercent(vitalsData.recovery_score)}
                    minValue={0}
                    maxValue={100}
                    ranges={recoveryIndexRange}
                    unit="%"
                    status={vitalsData.recovery_status}
                    healthyRange="More than 70%"
                    fromDate={fromDate}
                    setFromDate={setFromDate}
                    toDate={toDate}
                    setToDate={setToDate}
                    graphData={graphData}
                  />
                </div>
              </TabPane>)
            }
          </Tabs>
        </div>
      </div>

      {/* Consult a Doctor Banner */}
      {Array.isArray(vitalsData?.available_general_physicians) && vitalsData.available_general_physicians.length > 0 && (
        <div className="mt-10 mx-auto max-w-[118rem] px-2 sm:px-0">
          <h2 className="text-lg sm:text-xl font-bold text-center mb-3">Consult our doctors</h2>
          <div className="overflow-x-auto">
            <table
              className="w-full text-sm"
              style={{ borderCollapse: "collapse", border: "1px solid #d1d5db" }}
            >
              <tbody>
                <tr>
                  {vitalsData.available_general_physicians.map((doc) => (
                    <td key={doc.code} className="p-3 align-top" style={{ border: "1px solid #d1d5db" }}>
                      <a
                        href={doc.profile_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 font-semibold hover:underline"
                      >
                        {doc.name}
                      </a>
                      <p className="mb-0">{doc.qualification}</p>
                      <p className="mb-0">{doc.years_of_experience} years of experience</p>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-sm font-semibold text-center mt-3">
            To schedule an appointment with our doctors visit:{" "}
            <a
              href="https://curebay.com/services/book-an-appointment"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 font-normal hover:underline"
            >
              https://curebay.com/services/book-an-appointment
            </a>
          </p>
        </div>
      )}

      {/* Back Button */}
      {!props.isModal && (
        <div className="text-center mt-10">
          <button
            type="button"
            className="bg-[#0EA5E9] text-White px-8 py-3 rounded-lg font-semibold shadow-md hover:bg-[#0284C7] transition-all duration-300"
            onClick={() => { window.history.back(); }}
          >
            Back to Home
          </button>
        </div>
      )}
    </div>
  );
};

export default SmartVitalsReport;